import {
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Avatar,
  Divider,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  getReorderList,
  getGlobalReorderThreshold,
  setGlobalReorderThreshold,
  getProductReorderThresholds,
  setProductReorderThresholds,
} from "../api/adminStockApi";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SaveIcon from '@mui/icons-material/Save';
import { styled } from "@mui/material/styles";

interface ReorderItem {
  product_id: number;
  product_name: string;
  variant_id: number;
  variant_name: string;
  product_sku: string;
  image_url?: string;
  price: number;
  stock_type: "online" | "in_store";
  stock_count: number;
  reorder_threshold: number;
  supplier_id?: number;
  supplier_name?: string;
}

interface ProductThreshold {
  product_id: number;
  product_name: string;
  sku: string;
  reorder_threshold: number | null;
}

const ThresholdInput = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    textAlign: 'right',
    padding: theme.spacing(1),
    fontSize: '0.875rem',
  },
}));

const ReorderListPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [items, setItems] = useState<ReorderItem[]>([]);
  const [productThresholds, setProductThresholds] = useState<ProductThreshold[]>([]);
  const [globalThreshold, setGlobalThreshold] = useState<number>(2);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      const [global, productThresholdList] = await Promise.all([
        getGlobalReorderThreshold(),
        getProductReorderThresholds(),
      ]);
      const reorderList = await getReorderList(global);
      setItems(reorderList);
      setGlobalThreshold(global);
      setProductThresholds(productThresholdList);
    } catch (err) {
      console.error("Failed to fetch reorder data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportCSV = () => {
    const headers = [
      "Product",
      "Variant",
      "SKU",
      "Stock Type",
      "Stock Count",
      "Reorder Threshold",
      "Supplier",
    ];

    const groupedBySupplier: Record<string, ReorderItem[]> = {};

    items.forEach((item) => {
      const key = item.supplier_id?.toString() || "unknown";
      if (!groupedBySupplier[key]) groupedBySupplier[key] = [];
      groupedBySupplier[key].push(item);
    });

    Object.entries(groupedBySupplier).forEach(([_, group]) => {
      const supplierName = group[0]?.supplier_name || "Unknown Supplier";

      const rows = group.map((item) => [
        item.product_name,
        item.variant_name,
        item.product_sku,
        item.stock_type,
        item.stock_count,
        item.reorder_threshold,
        item.supplier_name || "",
      ]);

      const csv = [headers, ...rows]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const safeName = supplierName.replace(/\s+/g, "_").toLowerCase();
      link.setAttribute("download", `reorder_list_${safeName}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handleSaveThresholds = async () => {
    setSaving(true);
    try {
      await setGlobalReorderThreshold(globalThreshold);
      await setProductReorderThresholds(productThresholds);
      await fetchData();
      alert("Thresholds updated successfully.");
    } catch (err) {
      console.error("Failed to update thresholds", err);
      alert("Failed to save thresholds.");
    } finally {
      setSaving(false);
    }
  };

const renderDesktopView = () => (
  <Paper sx={{ p: 2, borderRadius: 2, boxShadow: theme.shadows[1] }}>
    <Grid container spacing={1} sx={{ fontWeight: 'bold', py: 1, borderBottom: '1px solid #eee' }}>
      <Grid item xs={2}>Product</Grid>
      <Grid item xs={1}>Variant</Grid>
      <Grid item xs={1}>SKU</Grid>
      <Grid item xs={1}>Stock Type</Grid>
      <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>Stock</Grid>
      <Grid item xs={2} sx={{ textAlign: 'right', pr: 3, minWidth: 150 }}>Reorder Threshold</Grid>
      <Grid item xs={2} sx={{ pl: 2 }}>Supplier</Grid>
    </Grid>
    
    {items.map((item, i) => {
      const productThreshold = productThresholds.find(
        (p) => p.product_id === item.product_id
      );

      return (
        <Grid container spacing={1} key={i} alignItems="center" sx={{ py: 2, borderBottom: '1px solid #f5f5f5' }}>
          <Grid item xs={2}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                src={item.image_url || '/placeholder.png'} 
                alt={item.variant_name}
                sx={{ width: 40, height: 40, mr: 2 }}
              />
              <Typography variant="body2">{item.product_name}</Typography>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="body2" color="text.secondary">{item.variant_name}</Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="body2">{item.product_sku}</Typography>
          </Grid>
          <Grid item xs={1}>
            <Chip 
              label={item.stock_type} 
              size="small" 
              color={item.stock_type === 'online' ? 'primary' : 'secondary'}
              sx={{ textTransform: 'capitalize' }}
            />
          </Grid>
          <Grid item xs={1} sx={{ textAlign: 'right', pr: 2 }}>
            <Typography 
              variant="body2" 
              fontWeight="medium"
              color={item.stock_count <= (productThreshold?.reorder_threshold || globalThreshold) ? 'error.main' : 'text.primary'}
            >
              {item.stock_count}
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ textAlign: 'right', pr: 3 }}>
            <ThresholdInput
              type="number"
              size="small"
              value={productThreshold?.reorder_threshold?.toString() || ''}
              onChange={(e) => {
                const value = e.target.value;
                setProductThresholds((prev) =>
                  prev.map((p) =>
                    p.product_id === item.product_id
                      ? {
                          ...p,
                          reorder_threshold: value === '' ? null : parseInt(value),
                        }
                      : p
                  )
                );
              }}
              sx={{ maxWidth: 120 }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 2 }}>
            <Typography 
              variant="body2" 
              color={!item.supplier_name ? 'error.main' : 'text.primary'}
            >
              {item.supplier_name || '⚠️ None Assigned'}
            </Typography>
          </Grid>
        </Grid>
      );
    })}
  </Paper>
);

  const renderMobileView = () => (
    <Grid container spacing={2}>
      {items.map((item, i) => {
        const productThreshold = productThresholds.find(
          (p) => p.product_id === item.product_id
        );

        return (
          <Grid item xs={12} key={i}>
            <Card sx={{ borderRadius: 2, boxShadow: theme.shadows[1] }}>
              <CardContent>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Avatar 
                    src={item.image_url || '/placeholder.png'} 
                    alt={item.variant_name}
                    sx={{ width: 56, height: 56, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">{item.product_name}</Typography>
                    <Typography variant="body2" color="text.secondary">{item.variant_name}</Typography>
                    <Typography variant="caption" color="text.secondary">SKU: {item.product_sku}</Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Stock Type</Typography>
                    <Chip 
                      label={item.stock_type} 
                      size="small" 
                      color={item.stock_type === 'online' ? 'primary' : 'secondary'}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Current Stock</Typography>
                    <Typography 
                      variant="body1" 
                      color={item.stock_count <= (productThreshold?.reorder_threshold || globalThreshold) ? 'error.main' : 'text.primary'}
                    >
                      {item.stock_count}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Reorder Threshold</Typography>
                    <ThresholdInput
                      fullWidth
                      type="number"
                      size="small"
                      value={productThreshold?.reorder_threshold?.toString() || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setProductThresholds((prev) =>
                          prev.map((p) =>
                            p.product_id === item.product_id
                              ? {
                                  ...p,
                                  reorder_threshold: value === '' ? null : parseInt(value),
                                }
                              : p
                          )
                        );
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography 
                      variant="body1" 
                      color={!item.supplier_name ? 'error.main' : 'text.primary'}
                    >
                      {item.supplier_name || '⚠️ None Assigned'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );

  return (
    <Box sx={{ p: isMobile ? 2 : 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 2 : 0,
      }}>
        <Typography variant="h5" fontWeight="bold">Reorder List</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleExportCSV} 
            disabled={loading}
            startIcon={<FileDownloadIcon />}
            sx={{ borderRadius: 2 }}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        alignItems: 'center', 
        mb: 3,
        flexDirection: isMobile ? 'column' : 'row',
      }}>
        <ThresholdInput
          label="Global Reorder Threshold"
          type="number"
          size="small"
          value={globalThreshold}
          onChange={(e) => setGlobalThreshold(parseInt(e.target.value))}
          sx={{ minWidth: isMobile ? '100%' : 200 }}
        />
        <Button 
          variant="contained" 
          onClick={handleSaveThresholds} 
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ 
            borderRadius: 2,
            px: 3,
            minWidth: isMobile ? '100%' : 'auto',
          }}
        >
          Save Thresholds
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>No low stock products found.</Typography>
        </Paper>
      ) : isMobile ? renderMobileView() : renderDesktopView()}
    </Box>
  );
};

export default ReorderListPage;